/* eslint-disable jsx-a11y/alt-text */
import { useNavigate, useSearchParams } from "react-router-dom";
import SearchInput from "src/components/widgets/SearchInput";
import BackIcon from "../images/back.png";
import { renderCurrency, renderNumber, showSuccessToast } from "src/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getArchivedInventory, unarchiveInventory } from "src/api/inventory";
import CustomDataTable from "src/components/CustomDataTable";
import CustomSheet from "src/components/CustomSheet";
import ProductDetailModal from "src/components/modals/ProductDetailModal";
import CustomConfirmDialog from "src/components/CustomConfirmDialog";
import CustomDropdown from "src/components/CustomDropdown";
import { Button } from "src/components/ui/button";
import { EllipsisIcon } from "src/svg";
import CustomSelect from "src/components/CustomSelect";
import { useDashboardContext } from "src/context/DashboardContext";

const ArchivedInventory = () => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { entities } = useDashboardContext();
	const [searchParams, setSearchParams] = useSearchParams();

	const search = searchParams.get("search");
	const page = searchParams.get("page") || 1;
	const limit = searchParams.get("limit") || 10;
	const entity = searchParams.get("entity") ?? "";

	const updateSearchParams = (key, value) => {
		const newSearchParams = new URLSearchParams(searchParams);

		if (value === "0") {
			newSearchParams.delete(key);
		} else {
			newSearchParams.set(key, value);
		}

		setSearchParams(newSearchParams);
	};

	const { data, isLoading } = useQuery({
		queryKey: ["inventory", "archived", page, limit, search],
		queryFn: () => getArchivedInventory({ page, limit, search }),
	});

	const { mutate: archiveProduct, isLoading: isArchiveLoading } = useMutation(unarchiveInventory, {
		onSuccess: () => {
			showSuccessToast("Product archived successfully");
			queryClient.invalidateQueries(["inventory"]);
		},
	});

	const columns = [
		{ name: "Item Code", selector: (row) => row.item_id },
		{ name: "Product Name", selector: (row) => row.name, grow: 1.25 },
		{ name: "Qty", selector: (row) => renderNumber(row.available_qty), grow: 0.75 },
		{ name: "SKU", selector: (row) => row.sku, grow: 0.75 },
		{ name: "Cost Price", selector: (row) => renderCurrency(row.cost_price) },
		{ name: "Selling Price", selector: (row) => renderCurrency(row.price) },
		{
			name: "Action",
			cell: (row) => (
				<CustomDropdown
					trigger={
						<Button variant="hover">
							<EllipsisIcon />
						</Button>
					}
					actions={[
						{
							label: (
								<CustomSheet title="Product Detail" trigger={<span>Detail</span>}>
									<ProductDetailModal product={row} />
								</CustomSheet>
							),
						},
						{
							label: (
								<CustomConfirmDialog trigger={<span>Unarchive Product</span>} mutateFn={() => archiveProduct({ uuid: row.uuid })} />
							),
							disabled: isArchiveLoading,
							variant: "destructive",
						},
					]}
				/>
			),
			maxWidth: "100px",
		},
	];

	const inventory = data?.data?.data ?? [];
	const totalRow = data?.data?.total;

	return (
		<div className="pageWrapper">
			<div className="mainContent">
				<div className="pageTop flex-cont flex-space-between flex-align-center">
					<div className="pageTop">
						<button className="transparent-btn" onClick={() => navigate("..", { relative: "path" })}>
							<img src={BackIcon} />
						</button>
						<h2>Archived Product</h2>
					</div>

					<div className="pagetop_right flex-cont flex-end flex-align-center">
						<CustomSelect
							placeholder="Select Store/Warehouse"
							containerClassName="!tw-w-[230px]"
							value={entity}
							onChange={(value) => updateSearchParams("entity", value)}
							options={[{ value: "0", label: "All" }, ...entities?.map(({ name, uuid }) => ({ value: uuid, label: name }))]}
						/>
					</div>
				</div>

				<div className="innerContent">
					<ActionBar navigate={navigate} />

					<CustomDataTable columns={columns} data={inventory} progressPending={isLoading} paginationTotalRows={totalRow} />
				</div>
			</div>
		</div>
	);
};

const ActionBar = () => {
	return (
		<div className="inner-top !tw-justify-end">
			<SearchInput name="search" placeholder="Search..." />
		</div>
	);
};

export default ArchivedInventory;
